import type { paths } from '@/types/generated/openapi';

// Union of all OpenAPI path strings, e.g. '/api/v1/organization'.
type OpenApiPath = keyof paths;

// Remove the '/api' prefix so runtime URLs match the axios baseURL setup
// (baseURL is '/api' in dev and `${VITE_API_BASE_URL}` — which ends in '/api' — in prod).
type StripApiPrefix<T extends string> = T extends `/api${infer Rest}` ? Rest : T;

// Path templates that contain at least one '{param}' placeholder.
type PathWithParam = Extract<OpenApiPath, `${string}{${string}}${string}`>;

// Replace every '{param}' placeholder with a concrete string segment in the return type.
type ReplaceParams<T extends string> = T extends `${infer A}{${string}}${infer B}`
  ? `${A}${string}${ReplaceParams<B>}`
  : T;

const encodeSegment = (value: string): string => encodeURIComponent(value);

// Runtime helper that strips '/api' while keeping the return type aligned.
const stripApiPrefix = <T extends OpenApiPath>(path: T): StripApiPrefix<T> =>
  (path.startsWith('/api/') ? path.slice(4) : path) as StripApiPrefix<T>;

// Inject one or more '{param}' values into a template (URL-encoded) and strip '/api'.
const withParams = <T extends PathWithParam>(
  template: T,
  params: Record<string, string>,
): ReplaceParams<StripApiPrefix<T>> => {
  let result: string = stripApiPrefix(template);
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`{${key}}`, encodeSegment(value));
  }
  return result as ReplaceParams<StripApiPrefix<T>>;
};

// Single-param convenience wrapper.
const withId = <T extends PathWithParam>(
  template: T,
  params: { id: string; paramName: string },
): ReplaceParams<StripApiPrefix<T>> => withParams(template, { [params.paramName]: params.id });

// Entity-scoped paths, derived from the generated spec so every literal below is
// compile-checked against `keyof paths`. Static routes use `stripApiPrefix`;
// parameterized routes are functions that inject (and encode) their params.
const apiPaths = {
  superAdmin: {
    currentContext: stripApiPrefix('/api/v1/super-admin/current-context'),
  },

  user: {
    root: stripApiPrefix('/api/v1/user'),
    skipDemographics: stripApiPrefix('/api/v1/user/me/demographics/skip'),
    location: stripApiPrefix('/api/v1/user/location'),
    dashboard: stripApiPrefix('/api/v1/user/dashboard'),
    signIn: stripApiPrefix('/api/v1/user/login'),
    signOut: stripApiPrefix('/api/v1/user/logout'),
    forgotPassword: stripApiPrefix('/api/v1/user/forgot-password'),
    uploadProfilePicture: stripApiPrefix('/api/v1/user/upload-profile-picture'),
    setInitialPassword: stripApiPrefix('/api/v1/user/set-password'),
    verifyEmail: stripApiPrefix('/api/v1/user/verify-email'),
    resetPassword: stripApiPrefix('/api/v1/user/reset-password'),
    verifyAuthToken: stripApiPrefix('/api/v1/user/verify-token'),
    verifyResetAuthToken: stripApiPrefix('/api/v1/user/verify-reset-token'),
    deleteOwnAccount: stripApiPrefix('/api/v1/user/me'),
    notificationPreferences: stripApiPrefix('/api/v1/user/notification-preferences'),
    resendVerificationEmail: stripApiPrefix('/api/v1/user/resend-verification-email'),
  },

  organization: {
    root: stripApiPrefix('/api/v1/organization'),
    my: stripApiPrefix('/api/v1/organization/my'),
    reports: stripApiPrefix('/api/v1/organizationreports'),
    dashboard: stripApiPrefix('/api/v1/organization/dash/dashboard'),
    activeContract: stripApiPrefix('/api/v1/organization/dash/dashboard/contract'),
    peerSupportEngagement: stripApiPrefix('/api/v1/organization/dash/peersupport-engagement'),
    teamConcerns: stripApiPrefix('/api/v1/organization/dash/team-concerns'),
    dashboardOld: stripApiPrefix('/api/v1/organization/my/dashboard'),
    peerSupportMembers: stripApiPrefix('/api/v1/organization/my/peer-support'),
    peerSupportLeads: stripApiPrefix('/api/v1/organization/my/peer-support-leads'),
    inviteUsers: stripApiPrefix('/api/v1/organization/invite/users'),
    members: stripApiPrefix('/api/v1/organization/my/users'),
    uploadLogo: stripApiPrefix('/api/v1/organization/my/upload-logo'),
    byId: (id: string) => withId('/api/v1/organization/{id}', { id, paramName: 'id' }),
    employeeById: (employeeId: string) =>
      withId('/api/v1/organization/employee/{employeeId}', { id: employeeId, paramName: 'employeeId' }),
    memberCareById: (id: string) => withId('/api/v1/organization/membercare/{id}', { id, paramName: 'id' }),
    peerSupportMemberById: (id: string) => withId('/api/v1/organization/my/peer-support/{id}', { id, paramName: 'id' }),
    reinvite: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/reinvite/{userId}', { organizationId, userId }),
    revokeInvitation: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/revoke/{userId}', { organizationId, userId }),
    resolveInvitationRevoke: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/revoke/{userId}/resolve', { organizationId, userId }),
    userAffiliation: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/affiliation', { organizationId, userId }),
    assignPeerSupportLead: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/assign-peer-support-lead', {
        organizationId,
        userId,
      }),
    revokePeerSupportLead: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/revoke-peer-support-lead', {
        organizationId,
        userId,
      }),
    upgradeToPeerSupport: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/upgrade-to-peer-support', {
        organizationId,
        userId,
      }),
    downgradeToEmployee: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/downgrade-to-employee', {
        organizationId,
        userId,
      }),
  },

  peerSupport: {
    profile: stripApiPrefix('/api/v1/peersupport/profile'),
    outreach: stripApiPrefix('/api/v1/peersupport/outreach'),
    request: stripApiPrefix('/api/v1/peersupport/request'),
    dashboard: stripApiPrefix('/api/v1/peersupport/dashboard'),
  },

  memberCare: {
    myOrganizationMemberCares: stripApiPrefix('/api/v1/membercare/organization/my/member-cares'),
  },

  providers: {
    root: stripApiPrefix('/api/v1/therapists'),
    matching: stripApiPrefix('/api/v1/therapists/matching'),
    byId: (id: string) => withId('/api/v1/therapists/{id}', { id, paramName: 'id' }),
    // NOTE: backend route is singular `therapist` (not `therapists`).
    sessionRequest: stripApiPrefix('/api/v1/therapist/request'),
  },

  ehrProviders: {
    root: stripApiPrefix('/api/v1/ehr/providers'),
    byId: (providerId: string) => withId('/api/v1/ehr/providers/{userId}', { id: providerId, paramName: 'userId' }),
    timeSlots: (providerId: string) =>
      withId('/api/v1/ehr/providers/{providerId}/time-slots', { id: providerId, paramName: 'providerId' }),
  },

  notifications: {
    root: stripApiPrefix('/api/v1/notifications'),
    unreadCount: stripApiPrefix('/api/v1/notifications/count'),
    markAllRead: stripApiPrefix('/api/v1/notifications/mark-all-read'),
    markMultipleRead: stripApiPrefix('/api/v1/notifications/mark-multiple-read'),
    reportMissingPeerSupport: stripApiPrefix('/api/v1/notifications/report-missing-peer-support'),
    sendScreenerReminder: stripApiPrefix('/api/v1/notifications/send-screener-reminder'),
    byId: (id: string) => withId('/api/v1/notifications/{id}', { id, paramName: 'id' }),
    readStatus: (id: string) => withId('/api/v1/notifications/{id}/read-status', { id, paramName: 'id' }),
  },

  notes: {
    root: stripApiPrefix('/api/v1/notes'),
    self: stripApiPrefix('/api/v1/notes/user/self'),
    byId: (id: string) => withId('/api/v1/notes/{id}', { id, paramName: 'id' }),
  },

  timeline: {
    self: stripApiPrefix('/api/v1/timeline/self'),
  },

  legal: {
    acceptanceStatus: stripApiPrefix('/api/v1/legal-documents/acceptance-status'),
    activeByType: (type: string) => withId('/api/v1/legal-documents/active/{type}', { id: type, paramName: 'type' }),
    acceptById: (id: string) => withId('/api/v1/legal-documents/accept/{id}', { id, paramName: 'id' }),
  },

  meta: {
    usStates: stripApiPrefix('/api/v1/meta/us-states'),
  },

  assessments: {
    bySlug: (slug: string) => withId('/api/v1/assessments/assessment/slug/{slug}', { id: slug, paramName: 'slug' }),
  },

  result: {
    last: stripApiPrefix('/api/v1/assessments/result/last'),
    create: stripApiPrefix('/api/v1/assessments/result/create'),
  },

  screener: {
    domainInterpretation: (domain: string) =>
      withId('/api/v1/screener/interpretation/{domain}', { id: domain, paramName: 'domain' }),
    subdomainInterpretation: (domain: string, subdomain: string) =>
      withParams('/api/v1/screener/interpretation/{domain}/{subdomain}', { domain, subdomain }),
  },

  publicScreener: {
    checkOrganization: stripApiPrefix('/api/v1/public-screener/check-organization'),
    sendResultsEmail: stripApiPrefix('/api/v1/public-screener/send-results-email'),
    validateOrg: (slug: string) =>
      withId('/api/v1/public-screener/org/{slug}/validate', { id: slug, paramName: 'slug' }),
    orgScore: (slug: string) => withId('/api/v1/public-screener/org/{slug}/score', { id: slug, paramName: 'slug' }),
    orgSubmit: (slug: string) => withId('/api/v1/public-screener/org/{slug}/submit', { id: slug, paramName: 'slug' }),
    resultsByToken: (token: string) =>
      withId('/api/v1/public-screener/results/{token}', { id: token, paramName: 'token' }),
  },

  cortex: {
    generate: (organizationId: string) =>
      withId('/api/v1/cortex-insights/generate/{organizationId}', { id: organizationId, paramName: 'organizationId' }),
  },

  growthZone: {
    goals: stripApiPrefix('/api/v1/goals'),
    goalById: (goalId: string) => withId('/api/v1/goals/{goalId}', { id: goalId, paramName: 'goalId' }),
    toggleGoal: (goalId: string) => withId('/api/v1/goals/{goalId}/toggle', { id: goalId, paramName: 'goalId' }),
  },

  contact: {
    contractSales: stripApiPrefix('/api/v1/contact/contract-sales'),
    // The recipient discriminator IS the path segment; both literals stay compile-checked.
    byRecipient: (recipient: 'member-care' | 'peer-support') =>
      recipient === 'member-care'
        ? stripApiPrefix('/api/v1/contact/member-care')
        : stripApiPrefix('/api/v1/contact/peer-support'),
  },

  contentCreatorApplications: {
    me: stripApiPrefix('/api/v1/content-creator-applications/me'),
    checkSocialLink: stripApiPrefix('/api/v1/content-creator-applications/social-links/check'),
    photo: stripApiPrefix('/api/v1/content-creator-applications/photo'),
    submit: stripApiPrefix('/api/v1/content-creator-applications/submit'),
    public: {
      me: stripApiPrefix('/api/v1/public/content-creator-applications/me'),
      checkSocialLink: stripApiPrefix('/api/v1/public/content-creator-applications/social-links/check'),
      photo: stripApiPrefix('/api/v1/public/content-creator-applications/photo'),
      start: stripApiPrefix('/api/v1/public/content-creator-applications/start'),
      submit: stripApiPrefix('/api/v1/public/content-creator-applications/submit'),
      verify: stripApiPrefix('/api/v1/public/content-creator-applications/verify'),
      recover: stripApiPrefix('/api/v1/public/content-creator-applications/recover'),
    },
  },

  creator: {
    profile: stripApiPrefix('/api/v1/creators/me/profile'),
    profilePhoto: stripApiPrefix('/api/v1/creators/me/profile/photo'),
  },

  // Creator content lifecycle (POST /content creates a draft authored by the creator).
  content: {
    root: stripApiPrefix('/api/v1/content'),
    me: stripApiPrefix('/api/v1/content/me'),
    author: (userId: string) => withId('/api/v1/content/authors/{userId}', { id: userId, paramName: 'userId' }),

    // --- Personalized feed algorithm surfaces ---
    // The server resolves personalization (feature flag, rollout bucket, holdout) itself;
    // the client always calls the personalized endpoint and gets an identical response
    // shape either way, so there is nothing to branch on here.
    personalizedFeed: stripApiPrefix('/api/v1/content/personalized-feed'),
    filterOptions: stripApiPrefix('/api/v1/content/filter-options'),
    filtered: stripApiPrefix('/api/v1/content/filtered'),
    search: stripApiPrefix('/api/v1/content/search'),
    focusedFeed: stripApiPrefix('/api/v1/content/focused-feed'),
    focusedView: stripApiPrefix('/api/v1/content/focused-view'),

    // --- Viewer reactions ---
    saved: stripApiPrefix('/api/v1/content/saved'),
    like: (id: string) => withId('/api/v1/content/{id}/like', { id, paramName: 'id' }),
    save: (id: string) => withId('/api/v1/content/{id}/save', { id, paramName: 'id' }),
    share: (id: string) => withId('/api/v1/content/{id}/share', { id, paramName: 'id' }),
    report: (id: string) => withId('/api/v1/content/{id}/report', { id, paramName: 'id' }),
    notInterested: (id: string) => withId('/api/v1/content/{id}/not-interested', { id, paramName: 'id' }),

    byId: (id: string) => withId('/api/v1/content/{id}', { id, paramName: 'id' }),
    publish: (id: string) => withId('/api/v1/content/{id}/publish', { id, paramName: 'id' }),
    unpublish: (id: string) => withId('/api/v1/content/{id}/unpublish', { id, paramName: 'id' }),
    thumbnail: (id: string) => withId('/api/v1/content/{id}/thumbnail', { id, paramName: 'id' }),
    articleImage: (id: string) => withId('/api/v1/content/{id}/article-image', { id, paramName: 'id' }),
    videoMuxUploadUrl: (id: string) => withId('/api/v1/content/{id}/video/mux/upload-url', { id, paramName: 'id' }),
    videoYoutube: (id: string) => withId('/api/v1/content/{id}/video/youtube', { id, paramName: 'id' }),
  },
} as const;

export { apiPaths, stripApiPrefix, withId, withParams };
export type { OpenApiPath };
